import * as R from "ramda";
import moment from "moment";
import { createSelector } from "reselect";
import { getters } from "./model";
import { getters as PortalHomeGetters } from "Portal/NewPortal/PortalHome/model";
import { getDate7DaysAgo } from "./utils";
import { getters as TabGetters } from "ui-kit/Tabs";
import {
  TABS,
  TAB_IDS,
  GROUP_BY_IDS,
  HOME_TASK_CARD_TABS_INSTANCE_ID
} from "./constants";
import { getShouldShowSessions } from "Portal/NewPortal/PortalHome/selectors";

export const getActiveFilter = createSelector(getters.filters, filters =>
  R.find(f => f.selected, filters)
);

export const getFilteredSessionsTasks = createSelector(
  PortalHomeGetters.sessionsTasks,
  state =>
    TabGetters.selectedTab(state, {
      instanceId: HOME_TASK_CARD_TABS_INSTANCE_ID
    }),
  getActiveFilter,
  (sessionsTasks, selectedTab, activeFilter) => {
    return R.reduce(
      (acc, session) => {
        const { tasks = [] } = session || {};
        const taskFormatted = R.compose(R.flatten, R.values)(tasks);

        let actualResults = taskFormatted;

        const { id: actualFilter } = activeFilter || {};
        if (actualFilter && actualFilter === TABS.ALL) {
          if (R.length(actualResults) > 0) {
            return [...acc, { ...session, tasks: actualResults }];
          } else {
            return acc;
          }
        } else {
          const now = moment();
          const sevenDaysFromNow = moment().add(7, "day");

          const filteredTasks = R.filter(task => {
            const cond = R.prop(actualFilter, {
              [GROUP_BY_IDS.COMPLETE]: R.equals(task.status, actualFilter),
              [GROUP_BY_IDS.INCOMPLETE]: R.equals(task.status, actualFilter),
              [GROUP_BY_IDS.DUE_THIS_WEEK]:
                task.due_raw &&
                moment(new Date(task.due_raw)).isBetween(now, sevenDaysFromNow)
            });
            return cond;
          })(actualResults || []);
          if (R.length(filteredTasks) > 0) {
            return [...acc, { ...session, tasks: filteredTasks }];
          } else {
            return acc;
          }
        }
      },
      [],
      sessionsTasks
    );
  }
);

export const getActualFilter = createSelector(
  getActiveFilter,
  R.pathOr(null, ["id"])
);

const getFilteredTasks = (taskOrder, tasks, filter, getDueThisWeek) => {
  const taskFormatted = R.compose(R.flatten, R.values)(tasks);

  let actualResults = taskFormatted;

  if (filter !== TABS.ALL) {
    actualResults = R.filter(task => {
      const cond = R.prop(filter, {
        [GROUP_BY_IDS.COMPLETE]: R.equals(task.status, filter),
        [GROUP_BY_IDS.INCOMPLETE]: R.equals(task.status, filter),
        [GROUP_BY_IDS.DUE_THIS_WEEK]: R.any(
          R.propEq("id", task.id),
          getDueThisWeek
        )
      });
      return cond;
    })(actualResults || []);
  }

  return R.sortBy(task => taskOrder[task.id])(actualResults);
};

export const getAllTasks = createSelector(
  PortalHomeGetters.tasks,
  PortalHomeGetters.sessionsTasks,
  (tasks, sessionsTasks) => {
    const allTasks = [
      ...R.propOr([], "past_due", tasks),
      ...R.propOr([], "due_next_7_days", tasks),
      ...R.propOr([], "due_this_month", tasks),
      ...sessionsTasks
    ];
    return allTasks;
  }
);

export const getDueNext7Days = createSelector(
  PortalHomeGetters.tasks,
  R.compose(payload => [...R.propOr([], "due_next_7_days", payload)])
);

export const filteredTasksCount = createSelector(
  PortalHomeGetters.taskOrder,
  PortalHomeGetters.tasks,
  getDueNext7Days,
  getActualFilter,
  (taskOrder, tasks, getDueThisWeek, actualFilter) =>
    getFilteredTasks(taskOrder, tasks, actualFilter, getDueThisWeek).length
);

export const filteredTasks = createSelector(
  PortalHomeGetters.taskOrder,
  PortalHomeGetters.tasks,
  getDueNext7Days,
  state =>
    TabGetters.selectedTab(state, {
      instanceId: HOME_TASK_CARD_TABS_INSTANCE_ID
    }),
  getActualFilter,
  (taskOrder, tasks, getDueThisWeek, selectedTab, actualFilter) => {
    return getFilteredTasks(taskOrder, tasks, actualFilter, getDueThisWeek);
  }
);

export const getTabs = createSelector(
  filteredTasksCount,
  filteredTasks,
  getFilteredSessionsTasks,
  getShouldShowSessions,
  (
    filteredTasksCount,
    filteredTasks,
    filteredSessionsTasks,
    shouldShowSessions
  ) => {
    let tabsToUse = TAB_IDS;
    if (!shouldShowSessions) {
      tabsToUse = tabsToUse.filter(tab => tab.id !== TABS.SESSION);
    }

    return R.reduce(
      (acc, filter) => {
        const taskFormatted = R.compose(R.flatten, R.values)(filteredTasks);
        const totalSessionsTasks = R.reduce(
          (acc, session) => acc + R.length(session?.tasks),
          0,
          filteredSessionsTasks
        );

        if (filter.id === TABS.SESSION) {
          return [
            ...acc,
            {
              ...filter,
              title: `Sessions ${
                totalSessionsTasks ? `(${totalSessionsTasks})` : "(0)"
              }`
            }
          ];
        }

        if (filter.id === TABS.CONTACT) {
          return [
            ...acc,
            {
              ...filter,
              title: `My Tasks ${
                taskFormatted?.length ? `(${taskFormatted?.length})` : "(0)"
              }`
            }
          ];
        }

        if (filter.id === TABS.ALL) {
          const totalAllTasks = R.add(filteredTasksCount, totalSessionsTasks);
          return [
            ...acc,
            {
              ...filter,
              title: totalAllTasks > 0 ? `All (${totalAllTasks})` : "All"
            }
          ];
        }

        return acc;
      },
      [],
      tabsToUse
    );
  }
);

export const getTotalTaskCount = createSelector(
  PortalHomeGetters.tasks,
  PortalHomeGetters.sessionsTasks,
  (tasks, sessionsTasks) => {
    const countAllTasks = [
      ...R.propOr([], "past_due", tasks),
      ...R.propOr([], "due_next_7_days", tasks),
      ...R.propOr([], "due_this_month", tasks)
    ];

    const totalSessionsTasks = R.reduce(
      (acc, session) => acc + R.length(session.tasks),
      0,
      sessionsTasks
    );

    return R.add(R.length(countAllTasks), totalSessionsTasks);
  }
);

export const getPastDue = createSelector(
  PortalHomeGetters.tasks,
  R.compose(
    R.length,
    R.filter(date => getDate7DaysAgo(date.split("T")[0]) <= 7),
    R.map(R.prop("due_raw")),
    payload => [...R.propOr([], "past_due", payload)]
  )
);

export const getDueToday = createSelector(
  PortalHomeGetters.tasks,
  R.compose(
    R.length,
    R.filter(
      date =>
        moment(date).format("YYYY-MM-DD") ===
        moment(new Date())
          .utc()
          .format("YYYY-MM-DD")
    ),
    R.map(R.prop("due_raw")),
    payload => [
      ...R.propOr(
        [],
        "due_next_7_days",
        R.filter(task => !task.is_complete, payload)
      )
    ]
  )
);

export const getSubmissionsInstanceIds = createSelector(
  PortalHomeGetters.tasks,
  R.compose(R.map(R.prop("id")), payload => [
    ...R.propOr([], "past_due", payload),
    ...R.propOr([], "due_next_7_days", payload),
    ...R.propOr([], "due_this_month", payload)
  ])
);

export const getCountOfAllCompletedTasks = createSelector(
  PortalHomeGetters.tasks,
  R.compose(
    R.length,
    R.filter(is_complete => is_complete),
    R.map(R.prop("is_complete")),
    payload => [
      ...R.propOr([], "due_next_7_days", payload),
      ...R.propOr([], "past_due", payload),
      ...R.propOr(
        [],
        "due_this_month",
        R.filter(task => task.is_complete, payload)
      )
    ]
  )
);

export const getCountOfAllTasks = createSelector(
  PortalHomeGetters.tasks,
  R.compose(R.length, R.map(R.prop("is_complete")), payload => [
    ...R.propOr([], "due_next_7_days", payload),
    ...R.propOr([], "past_due", payload),
    ...R.propOr(
      [],
      "due_this_month",
      R.filter(task => task.is_complete, payload)
    )
  ])
);

export const getCountOfAllSections = createSelector(
  getters.tasks,
  R.compose(R.length, R.map(R.prop("id")), payload => [
    ...R.propOr([], "past_due", payload),
    ...R.propOr([], "due_next_7_days", payload),
    ...R.propOr([], "due_this_month", payload)
  ])
);
